import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dahfkcrp14a1h3mr6kr0";

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const auxValue = String(formData.get("website_alt") || "").trim();
    if (auxValue) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    formData.delete("website_alt");

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const code = parsed?.code;
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          isSpam ? "提交未通过验证，请稍后重试。" : String(serverMsg) || "提交失败，请稍后重试。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请检查网络后重试。");
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-lg border border-secondary-300 bg-secondary-50 p-8 text-center">
        <span className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-secondary-500 text-background-50">
          <i className="ri-check-line text-2xl"></i>
        </span>
        <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">来信已收到</h3>
        <p className="mt-2 text-sm leading-relaxed text-foreground-600">
          感谢你愿意把它说出来。我们会在 2 个工作日内回复，未经你许可不会公开任何内容。
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer border border-secondary-300 text-secondary-800 hover:bg-secondary-100 transition-colors"
        >
          <i className="ri-mail-add-line"></i>
          再写一封
        </button>
      </div>
    );
  }

  return (
    <form
      id="help-contact-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-2">
            怎么称呼你
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="可以用昵称或化名"
            className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-2">
            联系邮箱
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="用于接收我们的回复"
            className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300"
          />
        </div>
        <div>
          <label htmlFor="contact-identity" className="block text-sm font-medium text-foreground-800 mb-2">
            你的身份
          </label>
          <select
            id="contact-identity"
            name="identity"
            defaultValue="关注这一议题的读者"
            className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 cursor-pointer"
          >
            <option>正在经历网络暴力的当事人</option>
            <option>当事人的亲友</option>
            <option>关注这一议题的读者</option>
            <option>心理或法律从业者</option>
          </select>
        </div>
        <div>
          <label htmlFor="contact-anonymous" className="block text-sm font-medium text-foreground-800 mb-2">
            是否希望匿名处理
          </label>
          <select
            id="contact-anonymous"
            name="anonymous"
            defaultValue="希望匿名"
            className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 cursor-pointer"
          >
            <option>希望匿名</option>
            <option>可以使用化名</option>
            <option>不介意公开姓名</option>
          </select>
        </div>
      </div>

      <div className="mt-5">
        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-2">
          想说的话
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请描述你遇到的情况，或你想了解的帮助方向（500 字以内）"
          className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 resize-none"
        ></textarea>
        <p className="mt-2 text-xs text-foreground-500">最多 500 字，我们会严格保密。</p>
      </div>

      <div className="field-aux-note" aria-hidden="true">
        <label htmlFor="contact-website-alt">网站备注</label>
        <input
          id="contact-website-alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      {status === "error" && formError && (
        <p className="mt-4 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-4 py-3">
          {formError}
        </p>
      )}

      <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <i className="ri-send-plane-line"></i>
          {status === "submitting" ? "正在提交…" : "提交来信"}
        </button>
        <p className="text-xs text-foreground-500 leading-relaxed">
          提交即表示你理解：本平台无法提供紧急救助，如有安全风险请优先报警。
        </p>
      </div>
    </form>
  );
}